<template>
    <section class="request-dashboard">
  
      <!-- Review header stays outside the scrolling details -->
  
      <header class="dashboard-header">
  
        <div class="header-copy">
  
          <div class="header-eyebrow">
            Attorney Request
          </div>
  
          <h2>
            {{ primaryIssueTitle }}
          </h2>
  
          <div class="header-meta">
  
            <span
              class="status-pill"
              :class="statusClass(selectedStatus)"
            >
              {{ statusLabel(selectedStatus) }}
            </span>
  
            <!-- <span class="header-reference">
              {{ request.id }}
            </span> -->
  
          </div>
  
        </div>
  
        <div class="header-actions">
  
          <button
            type="button"
            class="back-button"
            :disabled="savingStatus"
            @click="$emit('cancel')"
          >
            Back to Requests
          </button>
  
          <button
            type="button"
            class="save-button"
            :disabled="
              savingStatus ||
              !hasStatusChanged
            "
            @click="saveStatus"
          >
            {{
              savingStatus
                ? "Saving..."
                : "Save Status"
            }}
          </button>
  
        </div>
  
      </header>
  
      <div class="dashboard-scroll">
  
        <!-- Overview -->
  
        <section class="overview-panel">
  
          <div class="overview-main">
  
            <div class="section-heading">
  
              <div>
                <h3>
                  Request Overview
                </h3>
  
                <p>
                  Review the request details and move it through the
                  attorney-assistance workflow.
                </p>
              </div>
  
            </div>
  
            <div class="overview-grid">
  
              <article class="overview-item">
  
                <span class="overview-label">
                  Received
                </span>
  
                <strong>
                  {{ relativeDate(request.createdAt) }}
                </strong>
  
                <span class="overview-detail">
                  {{ formatDate(request.createdAt) }}
                </span>
  
              </article>
  
              <article class="overview-item">
  
                <span class="overview-label">
                  Last Updated
                </span>
  
                <strong>
                  {{
                    request.updatedAt
                      ? relativeDate(request.updatedAt)
                      : "Not updated"
                  }}
                </strong>
  
                <span class="overview-detail">
                  {{
                    request.updatedAt
                      ? formatDate(request.updatedAt)
                      : "No review activity yet"
                  }}
                </span>
  
              </article>
  
              <article class="overview-item">
  
                <span class="overview-label">
                  Jurisdiction
                </span>
  
                <strong>
                  {{
                    request.jurisdiction ||
                    "Not provided"
                  }}
                </strong>
  
              </article>
  
            </div>
  
          </div>
  
          <aside class="status-panel">
  
            <div class="status-panel-heading">
  
              <div>
                <h3>
                  Review Status
                </h3>
  
                <p>
                  Track where the request is in the review process.
                </p>
              </div>
  
            </div>
  
            <label class="status-field">
  
              <span>
                Current Status
              </span>
  
              <select v-model="selectedStatus">
  
                <option
                  v-for="status in availableStatuses"
                  :key="status.value"
                  :value="status.value"
                >
                  {{ status.label }}
                </option>
  
              </select>
  
            </label>
  
            <div
              v-if="hasStatusChanged"
              class="unsaved-status"
            >
              Status change has not been saved.
            </div>
  
          </aside>
  
        </section>
  
        <!-- Request organization -->
  
        <section class="dashboard-card">
  
          <div class="section-heading">
  
            <div>
              <h3>
                Client Preferences
              </h3>
  
              <p>
                Client identity and contact preferences for the request.
              </p>
            </div>
  
          </div>
  
          <div class="pill-groups">

            <div class="pill-group client-info-group">

              <span class="pill-group-label">
                Client
              </span>

              <span
                v-if="loadingClientInfo"
                class="client-info-message"
              >
                Loading client information...
              </span>

              <div
                v-else-if="clientInfo"
                class="client-info"
              >

                <strong class="client-name">
                  {{ clientInfo.name || "Name unavailable" }}
                </strong>

                <a
                  v-if="clientInfo.mobile"
                  class="client-contact-link"
                  :href="`tel:${clientInfo.mobile}`"
                >
                  <span>Mobile</span>
                  {{ formatPhoneNumber(clientInfo.mobile) }}
                </a>

                <a
                  v-if="clientInfo.contactEm"
                  class="client-contact-link"
                  :href="`mailto:${clientInfo.contactEm}`"
                >
                  <span>Email</span>
                  {{ clientInfo.contactEm }}
                </a>

                <span
                  v-if="!clientInfo.mobile && !clientInfo.contactEm"
                  class="client-info-message"
                >
                  App contact only
                </span>

              </div>

              <span
                v-else
                class="client-info-message error-text"
              >
                Client information unavailable
              </span>

            </div>
  
            <div class="pill-group">
  
              <span class="pill-group-label">
                Contact
              </span>
  
              <div class="pill-row">
  
                <span
                  v-for="method in preferredContactMethods"
                  :key="method"
                  class="info-pill contact-pill"
                >
                  {{ contactLabel(method) }}
                </span>
  
                <span
                  v-if="preferredContactMethods.length === 0"
                  class="info-pill neutral-pill"
                >
                  No preference provided
                </span>
  
              </div>
  
            </div>
  
            <div class="pill-group">
  
              <span class="pill-group-label">
                Attorney Access
              </span>
  
              <div class="pill-row">
  
                <span
                  class="info-pill"
                  :class="
                    request.allowOutsideAttorneys
                      ? 'positive-pill'
                      : 'neutral-pill'
                  "
                >
                  {{
                    request.allowOutsideAttorneys
                      ? "Outside attorneys allowed"
                      : "Outside attorneys not allowed"
                  }}
                </span>
  
              </div>
  
            </div>
  
          </div>
  
        </section>
  
        <!-- Issues -->
  
        <section class="dashboard-card">
  
          <div class="section-heading">
  
            <div>
              <h3>
                Identified Issues
              </h3>
  
              <p>
                Issues prepared from the client’s request conversation.
              </p>
            </div>
  
            <span class="count-pill issue-count-pill">
              {{ requestIssues.length }}
            </span>
  
          </div>
  
          <div
            v-if="requestIssues.length === 0"
            class="empty-state"
          >
            No identifiable issues were saved with this request.
          </div>
  
          <div
            v-else
            class="issue-grid"
          >
  
            <article
              v-for="issue in requestIssues"
              :key="issue.id"
              class="issue-card"
            >
  
              <div class="issue-card-header">
  
                <span class="issue-topic">
                  {{ issue.topic }}
                </span>
  
                <span class="confidence-pill">
                  {{
                    formatProbability(
                      issue.probabilityScore
                    )
                  }}
                  confidence
                </span>
  
              </div>
  
              <p>
                {{
                  issue.summary ||
                  "No summary was provided."
                }}
              </p>
  
            </article>
  
          </div>
  
        </section>
  
        <!-- Internal notes -->
  
        <section class="dashboard-card wide-card">
  
          <div class="section-heading">
  
            <div>
              <h3>
                Internal Notes
              </h3>
  
              <p>
                Notes are visible only to the administrative and legal
                review teams.
              </p>
            </div>
  
            <span class="count-pill note-count-pill">
              {{ notes.length }}
            </span>
  
          </div>
  
          <form
            class="wide-entry-form"
            @submit.prevent="submitNote"
          >
  
            <label class="wide-field">
  
              <span>
                Add Internal Note
              </span>
  
              <textarea
                v-model="noteText"
                rows="5"
                placeholder="Record review activity, follow-up needs, referral information, or other internal context..."
              />
  
            </label>
  
            <div class="entry-footer">
  
              <span class="entry-helper">
                This note will not be visible to the client.
              </span>
  
              <button
                type="submit"
                class="primary-entry-button"
                :disabled="
                  addingNote ||
                  !canAddNote
                "
              >
                {{
                  addingNote
                    ? "Adding..."
                    : "Add Internal Note"
                }}
              </button>
  
            </div>
  
          </form>
  
          <div class="section-divider" />
  
          <div
            v-if="notes.length > 0"
            class="timeline-list"
          >
  
            <article
              v-for="note in sortedNotes"
              :key="note.id"
              class="timeline-card note-card"
            >
  
              <div class="timeline-card-header">
  
                <div class="timeline-pills">
  
                  <span class="type-pill internal-note-pill">
                    Internal Note
                  </span>
  
                  <span class="author-pill">
                    {{
                      note.authorId ||
                      "Unknown reviewer"
                    }}
                  </span>
  
                </div>
  
                <span class="timeline-date">
                  {{ formatDate(note.createdAt) }}
                </span>
  
              </div>
  
              <p class="timeline-copy">
                {{ note.text }}
              </p>
  
            </article>
  
          </div>
  
          <div
            v-else
            class="empty-state contained-empty"
          >
            No internal notes have been added.
          </div>
  
        </section>
  
        <MaixClientMessages
          :key="request.id"
          :request-id="request.id"
          :default-subject="primaryIssueTitle"
          @message-sent="onUnifiedMessageSent"
        />
  
        <p
          v-if="errorMessage"
          class="error-message"
        >
          {{ errorMessage }}
        </p>
  
      </div>
  
    </section>
  </template>
  
  <script>
  import {
  computed,
  onMounted,
  ref
} from "vue";
  
  import {
  addAttorneyRequestNote,
  fetchAttorneyRequestClientInfo,
  fetchAttorneyRequestMessages,
  fetchAttorneyRequestNotes,
  updateAttorneyRequestStatus
} from "@/firebase/firestore";

import MaixClientMessages from "@/components/messages/MaixClientMessages.vue";
  
  export default {
    name:
      "RequestDashboard",

    components: {
      MaixClientMessages
    },
  
    props: {
      request: {
        type:
          Object,
        required:
          true
      },
  
      adminId: {
        type:
          String,
        default:
          ""
      }
    },
  
    emits: [
      "cancel",
      "saved",
      "message-sent"
    ],
  
    setup(
      props,
      {
        emit
      }
    ) {
  
      const availableStatuses = [
        {
          value:
            "new",
          label:
            "New"
        },
        {
          value:
            "reviewing",
          label:
            "Reviewing"
        },
        {
          value:
            "needs-information",
          label:
            "Needs Information"
        },
        {
          value:
            "referred",
          label:
            "Referred"
        },
        {
          value:
            "accepted",
          label:
            "Accepted"
        },
        {
          value:
            "closed",
          label:
            "Closed"
        }
      ];
  
      const normalizeStatus =
        status => {
  
          if (
            typeof status !==
              "string" ||
            status.trim() ===
              ""
          ) {
            return "new";
          }
  
          const normalized =
            status
              .trim()
              .toLowerCase();
  
          return normalized ===
            "submitted"
            ? "new"
            : normalized;
        };
  
      const originalStatus =
        ref(
          normalizeStatus(
            props.request.status
          )
        );
  
      const selectedStatus =
        ref(
          originalStatus.value
        );

      const clientInfo =
        ref(null);

      const loadingClientInfo =
        ref(false);

      const loadRequestClientInfo =
        async () => {

          loadingClientInfo.value =
            true;

          try {

            clientInfo.value =
              await fetchAttorneyRequestClientInfo(
                props.request.id
              );

          } catch (error) {

            console.error(
              "Unable to load attorney request client information:",
              error
            );

            clientInfo.value =
              null;

          } finally {

            loadingClientInfo.value =
              false;
          }
        };

        const loadingActivity =
  ref(false);

const loadRequestActivity =
  async () => {

    loadingActivity.value =
      true;

    errorMessage.value =
      "";

    try {

      const [
        loadedNotes,
        loadedMessages
      ] =
        await Promise.all([
          fetchAttorneyRequestNotes(
            props.request.id
          ),

          fetchAttorneyRequestMessages(
            props.request.id
          )
        ]);

      notes.value =
        loadedNotes;

      requestMessages.value =
        loadedMessages;

    } catch (error) {

      console.error(
        "❌ Unable to load request activity:",
        error
      );

      errorMessage.value =
        "Unable to load notes and Request Messages.";

    } finally {

      loadingActivity.value =
        false;
    }
  };

onMounted(
  loadRequestActivity
);

onMounted(
  loadRequestClientInfo
);
  
      const notes =
        ref(
          Array.isArray(
            props.request.internalNotes
          )
            ? [...props.request.internalNotes]
            : []
        );
  
      const requestMessages =
        ref(
          Array.isArray(
            props.request.requestMessages
          )
            ? [...props.request.requestMessages]
            : []
        );
  
      const noteText =
        ref("");
  
      const savingStatus =
        ref(false);
  
      const addingNote =
        ref(false);
  
  
      const errorMessage =
        ref("");
  
      const requestIssues =
        computed(() => {
  
          return Array.isArray(
            props.request.issues
          )
            ? props.request.issues
            : [];
        });
  
      const primaryIssueTitle =
        computed(() => {
  
          if (
            requestIssues.value.length ===
              0
          ) {
            return "Attorney Assistance Request";
          }
  
          const primaryIssue =
            [...requestIssues.value]
              .sort(
                (
                  first,
                  second
                ) =>
                  (
                    second.probabilityScore ??
                    0
                  ) -
                  (
                    first.probabilityScore ??
                    0
                  )
              )[0];
  
          return (
            primaryIssue.topic ||
            "Attorney Assistance Request"
          );
        });
  
      const preferredContactMethods =
        computed(() => {
  
          return Array.isArray(
            props.request.preferredContactMethods
          )
            ? props.request.preferredContactMethods
            : [];
        });

      const hasStatusChanged =
        computed(() => {
  
          return (
            selectedStatus.value !==
            originalStatus.value
          );
        });
  
      const canAddNote =
        computed(() => {
  
          return (
            noteText.value
              .trim() !==
            ""
          );
        });
  
      const sortedNotes =
        computed(() => {
  
          return [...notes.value]
            .sort(
              (
                first,
                second
              ) =>
                dateValue(second.createdAt) -
                dateValue(first.createdAt)
            );
        });
  
      const saveStatus =
        async () => {
  
          if (
            !hasStatusChanged.value ||
            savingStatus.value
          ) {
            return;
          }
  
          savingStatus.value =
            true;
  
          errorMessage.value =
            "";
  
          try {
  
            const saved =
              await updateAttorneyRequestStatus(
                props.request.id,
                selectedStatus.value
              );
  
            if (!saved) {
              throw new Error(
                "Status was not saved."
              );
            }
  
            originalStatus.value =
              selectedStatus.value;
  
            emit(
              "saved",
              {
                ...props.request,
  
                status:
                  selectedStatus.value,
  
                internalNotes:
                  notes.value,
  
                requestMessages:
                  requestMessages.value
              }
            );
  
          } catch (error) {
  
            console.error(
              "❌ Unable to save request status:",
              error
            );
  
            errorMessage.value =
              "Unable to save the request status.";
  
          } finally {
  
            savingStatus.value =
              false;
          }
        };
  
      const submitNote =
        async () => {
  
          if (
            !canAddNote.value ||
            addingNote.value
          ) {
            return;
          }
  
          addingNote.value =
            true;
  
          errorMessage.value =
            "";
  
          try {
  
            const note =
              await addAttorneyRequestNote(
                props.request.id,
                {
                  text:
                    noteText.value,
  
                  authorId:
                    props.adminId
                }
              );
  
            if (!note) {
              throw new Error(
                "Note was not saved."
              );
            }
  
            notes.value.push(
              note
            );
  
            noteText.value =
              "";
  
          } catch (error) {
  
            console.error(
              "❌ Unable to add request note:",
              error
            );
  
            errorMessage.value =
              "Unable to add the internal note.";
  
          } finally {
  
            addingNote.value =
              false;
          }
        };
  
      const onUnifiedMessageSent = async message => {
        await loadRequestActivity();
        emit("message-sent", message);
      };
  
      const statusLabel =
        status => {
  
          const matchingStatus =
            availableStatuses.find(
              option =>
                option.value ===
                normalizeStatus(status)
            );
  
          return (
            matchingStatus?.label ??
            "New"
          );
        };
  
      const statusClass =
        status => {
  
          return `status-${normalizeStatus(
            status
          )}`;
        };
  
      const contactLabel =
        method => {
  
          const normalized =
            String(
              method ??
              ""
            )
              .trim()
              .toLowerCase();
  
          switch (normalized) {
  
          case "sms":
          case "text":
            return "Text";
  
          case "phone":
          case "call":
            return "Phone";
  
          case "email":
            return "Email";
  
          case "app":
            return "App";
  
          default:
            return method;
          }
        };

      const formatPhoneNumber =
        value => {

          if (!value) {
            return "";
          }

          const digits =
            String(value)
              .replace(/\D/g, "");

          const phoneDigits =
            digits.length === 11 &&
            digits.startsWith("1")
              ? digits.slice(1)
              : digits;

          if (phoneDigits.length !== 10) {
            return value;
          }

          return `(${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6)}`;
        };
  
      const dateValue =
        value => {
  
          if (!value) {
            return 0;
          }
  
          const date =
            typeof value.toDate ===
              "function"
              ? value.toDate()
              : new Date(value);
  
          return Number.isNaN(
            date.getTime()
          )
            ? 0
            : date.getTime();
        };
  
      const dateFromValue =
        value => {
  
          if (!value) {
            return null;
          }
  
          const date =
            typeof value.toDate ===
              "function"
              ? value.toDate()
              : new Date(value);
  
          return Number.isNaN(
            date.getTime()
          )
            ? null
            : date;
        };
  
      const formatDate =
        value => {
  
          const date =
            dateFromValue(
              value
            );
  
          if (!date) {
            return "Unknown";
          }
  
          return date.toLocaleString(
            undefined,
            {
              dateStyle:
                "medium",
  
              timeStyle:
                "short"
            }
          );
        };
  
      const relativeDate =
        value => {
  
          const date =
            dateFromValue(
              value
            );
  
          if (!date) {
            return "Unknown";
          }
  
          const elapsed =
            Date.now() -
            date.getTime();
  
          const minutes =
            Math.floor(
              elapsed /
              60_000
            );
  
          const hours =
            Math.floor(
              elapsed /
              3_600_000
            );
  
          const days =
            Math.floor(
              elapsed /
              86_400_000
            );
  
          if (minutes < 1) {
            return "Just now";
          }
  
          if (minutes < 60) {
            return `${minutes}m ago`;
          }
  
          if (hours < 24) {
            return `${hours}h ago`;
          }
  
          if (days === 1) {
            return "Yesterday";
          }
  
          if (days < 7) {
            return `${days} days ago`;
          }
  
          if (days < 30) {
  
            const weeks =
              Math.floor(
                days /
                7
              );
  
            return `${weeks} ${
              weeks === 1
                ? "week"
                : "weeks"
            } ago`;
          }
  
          if (days < 365) {
  
            const months =
              Math.floor(
                days /
                30
              );
  
            return `${months} ${
              months === 1
                ? "month"
                : "months"
            } ago`;
          }
  
          const years =
            Math.floor(
              days /
              365
            );
  
          return `${years} ${
            years === 1
              ? "year"
              : "years"
          } ago`;
        };
  
      const formatProbability =
        probability => {
  
          const value =
            Number(
              probability
            );
  
          if (
            Number.isNaN(
              value
            )
          ) {
            return "0%";
          }
  
          return `${Math.round(
            value *
            100
          )}%`;
        };
  
      return {
        availableStatuses,
        selectedStatus,
  
        notes,
        sortedNotes,
        noteText,
  
        requestMessages,

        loadingActivity,
        loadRequestActivity,
  
        savingStatus,
        addingNote,
        errorMessage,
  
        requestIssues,
        primaryIssueTitle,
        preferredContactMethods,
        clientInfo,
        loadingClientInfo,
  
        hasStatusChanged,
        canAddNote,
  
        saveStatus,
        submitNote,
        onUnifiedMessageSent,
  
        statusLabel,
        statusClass,
        contactLabel,
        formatPhoneNumber,
        formatDate,
        relativeDate,
        formatProbability
      };
    }
  };
  </script>
  
  <style scoped>
  .request-dashboard {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    background: #f6f8f7;
  }
  
  /* Header */
  
  .dashboard-header {
    flex: 0 0 auto;
  
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
  
    padding: 20px 24px;
    border-bottom: 1px solid #dde3df;
    background: #ffffff;
  }
  
  .header-copy {
    min-width: 0;
  }
  
  .header-eyebrow {
    margin-bottom: 5px;
    color: #7b8680;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  
  .header-copy h2 {
    margin: 0;
    color: #192720;
    font-size: 1.35rem;
  }
  
  .header-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 9px;
    margin-top: 9px;
  }
  
  .header-reference {
    max-width: 360px;
    overflow: hidden;
    color: #909a95;
    font-size: 0.76rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .header-actions {
    display: flex;
    flex-shrink: 0;
    gap: 11px;
  }
  
  .back-button,
  .save-button {
    min-width: 140px;
    padding: 11px 17px;
    border: 0;
    border-radius: 9px;
    color: #ffffff;
    font: inherit;
    font-weight: 700;
    cursor: pointer;
  }
  
  .back-button {
    background: #d64545;
  }
  
  .back-button:hover:not(:disabled) {
    background: #bd3939;
  }
  
  .save-button {
    background: #18ab69;
  }
  
  .save-button:hover:not(:disabled) {
    background: #15945c;
  }
  
  .back-button:disabled,
  .save-button:disabled {
    cursor: not-allowed;
    opacity: 0.52;
  }
  
  /* Main scrolling body */
  
  .dashboard-scroll {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    gap: 22px;
    padding: 24px;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .dashboard-scroll > * {
    flex-shrink: 0;
  }
  
  /* Overview */
  
  .overview-panel {
    display: grid;
    grid-template-columns:
      minmax(0, 1fr)
      minmax(260px, 330px);
    gap: 20px;
  }
  
  .overview-main,
  .status-panel,
  .dashboard-card {
    border: 1px solid #dee5e1;
    border-radius: 14px;
    background: #ffffff;
    box-shadow: 0 5px 18px rgba(29, 49, 39, 0.04);
  }
  
  .overview-main {
    padding: 21px;
  }
  
  .overview-grid {
    display: grid;
    grid-template-columns:
      repeat(
        3,
        minmax(0, 1fr)
      );
    gap: 12px;
    margin-top: 18px;
  }
  
  .overview-item {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
    padding: 15px;
    border: 1px solid #e7ebe8;
    border-radius: 10px;
    background: #fafcfa;
  }
  
  .overview-label {
    color: #7f8984;
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }
  
  .overview-item strong {
    color: #25332c;
    font-size: 0.92rem;
  }
  
  .overview-detail {
    color: #8c9691;
    font-size: 0.74rem;
  }
  
  .breakable {
    overflow-wrap: anywhere;
  }
  
  .status-panel {
    align-self: stretch;
    padding: 21px;
  }
  
  .status-panel-heading h3,
  .section-heading h3 {
    margin: 0;
    color: #1f2d26;
    font-size: 1.05rem;
  }
  
  .status-panel-heading p,
  .section-heading p {
    margin: 5px 0 0;
    color: #76817b;
    line-height: 1.4;
  }
  
  .status-field {
    display: flex;
    flex-direction: column;
    gap: 7px;
    margin-top: 20px;
    color: #34423a;
    font-size: 0.85rem;
    font-weight: 700;
  }
  
  .status-field select {
    width: 100%;
    padding: 12px 13px;
    border: 1px solid #d4ddd8;
    border-radius: 9px;
    background: #ffffff;
    color: #24312b;
    font: inherit;
  }
  
  .unsaved-status {
    margin-top: 12px;
    padding: 10px 11px;
    border-radius: 8px;
    background: #fff5db;
    color: #896717;
    font-size: 0.78rem;
    font-weight: 600;
  }
  
  /* General cards */
  
  .dashboard-card {
    padding: 21px;
  }
  
  .wide-card {
    width: 100%;
    box-sizing: border-box;
  }
  
  .section-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 18px;
    margin-bottom: 18px;
  }
  
  /* Pills */
  
  .pill-groups {
    display: grid;
    grid-template-columns:
      repeat(
        3,
        minmax(0, 1fr)
      );
    gap: 16px;
  }
  
  .pill-group {
    padding: 15px;
    border: 1px solid #e6ebe8;
    border-radius: 10px;
    background: #fafcfa;
  }
  
  .pill-group-label {
    display: block;
    margin-bottom: 10px;
    color: #77817c;
    font-size: 0.69rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .client-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 7px;
    min-width: 0;
  }

  .client-name {
    color: #28342e;
    font-size: 0.92rem;
    line-height: 1.3;
  }

  .client-contact-link {
    max-width: 100%;
    color: #17885a;
    font-size: 0.8rem;
    font-weight: 650;
    overflow-wrap: anywhere;
    text-decoration: none;
  }

  .client-contact-link:hover {
    text-decoration: underline;
  }

  .client-contact-link span {
    margin-right: 5px;
    color: #77817c;
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .client-info-message {
    color: #77817c;
    font-size: 0.8rem;
  }

  .client-info-message.error-text {
    color: #a83939;
  }
  
  .pill-row,
  .timeline-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
  }
  
  .info-pill,
  .type-pill,
  .author-pill,
  .count-pill,
  .confidence-pill,
  .status-pill {
    display: inline-flex;
    align-items: center;
    padding: 5px 9px;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 700;
  }
  
  .contact-pill {
    background: #eaf2ff;
    color: #2f67a8;
  }
  
  .positive-pill {
    background: #edf7f1;
    color: #178554;
  }
  
  .warning-pill {
    background: #fff0e8;
    color: #a35220;
  }
  
  .neutral-pill {
    background: #f0f2f1;
    color: #69736e;
  }
  
  /* Status */
  
  .status-new {
    background: #eaf2ff;
    color: #2f67a8;
  }
  
  .status-reviewing {
    background: #fff5db;
    color: #8c6817;
  }
  
  .status-needs-information {
    background: #fff0e8;
    color: #a35220;
  }
  
  .status-referred {
    background: #f0ebff;
    color: #684aa0;
  }
  
  .status-accepted {
    background: #edf7f1;
    color: #178554;
  }
  
  .status-closed {
    background: #f0f2f1;
    color: #66706b;
  }
  
  /* Issues */
  
  .issue-count-pill,
  .note-count-pill {
    background: #fff5db;
    color: #8c6817;
  }
  
  .message-count-pill {
    background: #eaf2ff;
    color: #2f67a8;
  }
  
  .issue-grid {
    display: grid;
    grid-template-columns:
      repeat(
        auto-fit,
        minmax(300px, 1fr)
      );
    gap: 14px;
  }
  
  .issue-card {
    padding: 17px;
    border: 1px solid #e3e8e5;
    border-radius: 11px;
    background: #fafcfa;
  }
  
  .issue-card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }
  
  .issue-topic {
    color: #26342d;
    font-weight: 750;
  }
  
  .confidence-pill {
    flex-shrink: 0;
    background: #edf7f1;
    color: #178554;
  }
  
  .issue-card p {
    margin: 11px 0 0;
    color: #616d66;
    line-height: 1.5;
  }
  
  /* Wide input sections */
  
  .wide-entry-form {
    display: flex;
    flex-direction: column;
    gap: 13px;
    width: 100%;
  }
  
  .wide-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    color: #34413a;
    font-weight: 700;
  }
  
  .wide-field textarea {
    display: block;
    width: 100%;
    min-height: 145px;
    box-sizing: border-box;
    padding: 14px 15px;
    border: 1px solid #d4ddd8;
    border-radius: 10px;
    background: #ffffff;
    color: #23312a;
    font: inherit;
    line-height: 1.5;
    resize: vertical;
  }
  
  .wide-field textarea:focus {
    border-color: #18ab69;
    outline: 3px solid rgba(24, 171, 105, 0.12);
  }
  
  .entry-footer,
  .message-controls {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 18px;
  }

  .message-controls {
    justify-content: space-between;
  }

  .sms-copy-control {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    color: #44524b;
    font-size: 0.9rem;
    font-weight: 700;
    cursor: pointer;
  }

  .sms-copy-control input {
    width: 18px;
    height: 18px;
    margin: 0;
    accent-color: #1689ec;
    cursor: pointer;
  }

  .sms-copy-control input:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .message-controls .primary-entry-button {
    margin-left: auto;
  }
  
  .entry-helper {
    color: #7c8681;
    font-size: 0.8rem;
  }
  
  .primary-entry-button {
    flex-shrink: 0;
    padding: 11px 18px;
    border: 0;
    border-radius: 9px;
    background: #18ab69;
    color: #ffffff;
    font: inherit;
    font-weight: 700;
    cursor: pointer;
  }
  
  .primary-entry-button:hover:not(:disabled) {
    background: #15945c;
  }
  
  .primary-entry-button:disabled {
    cursor: not-allowed;
    opacity: 0.52;
  }
  
  .section-divider {
    width: 100%;
    height: 1px;
    margin: 21px 0;
    background: #e6ebe8;
  }
  
  /* Timeline cards */
  
  .timeline-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  
  .timeline-card {
    padding: 16px 18px;
    border: 1px solid #e1e7e3;
    border-radius: 11px;
    background: #fafcfa;
  }
  
  .note-card {
    border-left: 4px solid #d4a62e;
  }
  
  .message-card {
    border-left: 4px solid #3685b5;
  }
  
  .timeline-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
  }
  
  .internal-note-pill {
    background: #fff5db;
    color: #8c6817;
  }
  
  .client-message-pill {
    background: #eaf2ff;
    color: #2f67a8;
  }
  
  .internal-message-pill {
    background: #f0ebff;
    color: #684aa0;
  }
  
  .delivery-pill {
    background: #edf7f1;
    color: #178554;
    text-transform: capitalize;
  }
  
  .author-pill {
    background: #f0f2f1;
    color: #69736e;
  }
  
  .timeline-date {
    flex-shrink: 0;
    color: #8a948f;
    font-size: 0.74rem;
  }
  
  .timeline-copy {
    margin: 13px 0 0;
    color: #344139;
    line-height: 1.55;
    white-space: pre-wrap;
  }
  
  /* States */
  
  .empty-state {
    padding: 19px;
    border: 1px dashed #d6ddd9;
    border-radius: 10px;
    background: #fafcfa;
    color: #7c8681;
  }
  
  .contained-empty {
    min-height: auto;
  }
  
  .error-message {
    margin: 0;
    padding: 13px 15px;
    border: 1px solid #efc5c5;
    border-radius: 9px;
    background: #fff7f7;
    color: #a33636;
  }
  
  /* Responsive */
  
  @media (max-width: 1050px) {
    .overview-panel {
      grid-template-columns: 1fr;
    }
  
    .overview-grid {
      grid-template-columns:
        repeat(
          2,
          minmax(0, 1fr)
        );
    }
  
    .pill-groups {
      grid-template-columns: 1fr;
    }
  }
  
  @media (max-width: 720px) {
    .dashboard-header {
      align-items: flex-start;
      flex-direction: column;
    }
  
    .header-actions {
      width: 100%;
    }
  
    .back-button,
    .save-button {
      flex: 1;
      min-width: 0;
    }
  
    .dashboard-scroll {
      padding: 17px;
    }
  
    .overview-grid {
      grid-template-columns: 1fr;
    }
  
    .entry-footer,
    .message-controls,
    .timeline-card-header {
      align-items: stretch;
      flex-direction: column;
    }
  
    .primary-entry-button {
      width: 100%;
    }
  }
  
  @media (max-width: 480px) {
    .header-actions {
      flex-direction: column-reverse;
    }
  
    .back-button,
    .save-button {
      width: 100%;
    }
  }
  </style>
